/**
 * Audibility, as distinct from delivery.
 *
 * Packets arriving is not the same as sound being audible: a codec mismatch or a gateway fault
 * can deliver a perfect packet stream that decodes to silence. So the page listens to the actual
 * waveform on both streams and counts how long it was above the noise floor.
 */

/** Below this RMS the signal is indistinguishable from a quiet line. */
const SILENCE_THRESHOLD = 0.01

const SAMPLE_INTERVAL_MS = 100

export interface LevelMeter {
	/** Latest RMS, 0-1, for a live meter. */
	readonly level: number
	/** Peak RMS seen, so a brief spoken phrase is not averaged away. */
	readonly peak: number
	/** Milliseconds spent above the silence threshold. */
	readonly audibleMs: number
	/** Milliseconds sampled, so the result can say "heard N of M seconds". */
	readonly elapsedMs: number
	stop(): void
}

/**
 * Starts sampling a stream's loudness. Returns a live view plus a stop function; the caller owns
 * the AudioContext's lifetime through it.
 *
 * Chrome will not run an analyser over a remote stream that is not attached to a media element,
 * so the caller must attach the remote audio before calling this.
 */
export function startLevelMeter(stream: MediaStream): LevelMeter {
	const state = { level: 0, peak: 0, audibleMs: 0, elapsedMs: 0 }

	// Safari still only exposes the prefixed constructor.
	const AudioContextCtor = window.AudioContext
		?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

	if (!AudioContextCtor || stream.getAudioTracks().length === 0) {
		return { ...state, stop() {} }
	}

	const context = new AudioContextCtor()
	const analyser = context.createAnalyser()
	analyser.fftSize = 2048
	const source = context.createMediaStreamSource(stream)
	source.connect(analyser)

	const buffer = new Float32Array(analyser.fftSize)
	const timer = setInterval(() => {
		analyser.getFloatTimeDomainData(buffer)

		let sumOfSquares = 0
		for (const value of buffer) sumOfSquares += value * value
		const rms = Math.sqrt(sumOfSquares / buffer.length)

		state.level = rms
		state.peak = Math.max(state.peak, rms)
		state.elapsedMs += SAMPLE_INTERVAL_MS
		if (rms > SILENCE_THRESHOLD) state.audibleMs += SAMPLE_INTERVAL_MS
	}, SAMPLE_INTERVAL_MS)

	return {
		get level() { return state.level },
		get peak() { return state.peak },
		get audibleMs() { return state.audibleMs },
		get elapsedMs() { return state.elapsedMs },
		stop() {
			clearInterval(timer)
			source.disconnect()
			void context.close().catch(() => {})
		}
	}
}
